/**
 * AI module API client — Sprint AI-MODULE v2.
 *
 * Two flavors of call here:
 *   - regular JSON CRUD via apiFetch (threads, credentials, memory, etc.)
 *   - one SSE stream consumer for the chat turn endpoint
 */
import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

// ── Per-user prefs + tenant credential status ──────────────────────

export interface UserAiPrefs {
  model_id: string | null;
  effort: string | null;
  reasoning_display: string | null;
  tenant_default_model_id: string | null;
  tenant_default_effort: string | null;
  tenant_default_reasoning_display: string | null;
  tenant_model_floor: string | null;
  tenant_anthropic_connected: boolean;
}

export async function getMyAiPrefs(): Promise<UserAiPrefs> {
  return apiFetch<UserAiPrefs>('/mod/ai/prefs');
}

// ── Threads ──────────────────────────────────────────────────────────

export interface ChatThread {
  id: string;
  title: string | null;
  last_provider: string | null;
  last_model: string | null;
  last_message_at: string | null;
  created_at: string;
}

export async function listThreads(): Promise<ChatThread[]> {
  const res = await apiFetch<{ items: ChatThread[] }>('/mod/ai/chat/threads');
  return res.items;
}

export async function createThread(title?: string): Promise<ChatThread> {
  return apiFetch<ChatThread>('/mod/ai/chat/threads', {
    method: 'POST',
    body: JSON.stringify({ title: title ?? null }),
  });
}

export async function deleteThread(id: string): Promise<void> {
  await apiFetch(`/mod/ai/chat/threads/${id}`, { method: 'DELETE' });
}

// ── Messages ─────────────────────────────────────────────────────────

export interface CitationRef {
  id: number;
  kind: string;
  source_ref: string;
  label: string;
  url: string;
  snippet?: string;
  page_or_line?: string;
  last_modified?: string;
  provider?: string;
  // v2.1 (AI-41): file-shaped citations carry these so the chip can
  // expand into a metadata card without a second fetch.
  mime_type?: string;
  owner_name?: string;
  file_size_bytes?: number;
  parent_name?: string;
  // Set on cw_file citations: the cached_files row UUID. Used by
  // InlineCitationCard to fetch the proxied thumbnail/preview from
  // /mod/cw/files/{vortex_id}/thumbnail (same proxy Connected Search
  // uses) since provider X-Frame-Options blocks naive iframe embeds.
  vortex_id?: string;
  // v2.3 (AI-62/AI-64): VM-shaped citations carry workflow state +
  // blocker attribution so the inline card can render action-aware
  // CTAs without a second fetch.
  entity_state?: string;
  days_in_state?: number;
  prerequisites_met?: boolean;
  current_blocker_role?: string;
  current_blocker_user_name?: string;
  current_blocker_user_email?: string;
  role_hint?: string;
}

export interface ToolCallSummary {
  tool_use_id: string;
  slug: string;
  input: Record<string, unknown>;
  is_error: boolean;
  latency_ms: number;
  citation_count: number;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string | null;
  reasoning: string | null;
  citations: CitationRef[] | null;
  tool_calls: ToolCallSummary[] | null;
  provider: string | null;
  model_id: string | null;
  cached_input_tokens: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  created_at: string;
}

export async function getThreadMessages(threadId: string): Promise<ChatMessage[]> {
  const res = await apiFetch<{ items: ChatMessage[] }>(
    `/mod/ai/chat/threads/${threadId}/messages`,
  );
  return res.items;
}

// ── Suggested questions ──────────────────────────────────────────────

export interface SuggestedQuestion {
  label: string;
  prompt: string;
  kind: string;
}

export interface SuggestedQuestionsResponse {
  chips: SuggestedQuestion[];
  showcases: SuggestedQuestion[];
}

export async function getSuggestedQuestions(): Promise<SuggestedQuestionsResponse> {
  return apiFetch<SuggestedQuestionsResponse>('/mod/ai/chat/suggested-questions');
}

// ── Capabilities ─────────────────────────────────────────────────────

export interface CapabilityTool {
  slug: string;
  name: string;
  description: string;
  module: string;
  cites_sources: boolean;
}

export interface CapabilityContext {
  key: string;
  label: string;
  detail: string | null;
  count: number | null;
}

export interface CapabilitiesResponse {
  tools: CapabilityTool[];
  context: CapabilityContext[];
  summary: string;
}

export async function getCapabilities(): Promise<CapabilitiesResponse> {
  return apiFetch<CapabilitiesResponse>('/mod/ai/chat/capabilities');
}

// ── Memory ───────────────────────────────────────────────────────────

export async function confirmMemory(memoryId: string, accept: boolean): Promise<void> {
  await apiFetch(`/mod/ai/memory/${memoryId}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ accept }),
  });
}

// ── Write-tool confirmation (Sprint AI-MODULE v2.2 AI-52) ────────────

export async function confirmTool(
  threadId: string,
  toolUseId: string,
  decision: 'confirm' | 'cancel',
  argumentOverrides?: Record<string, unknown>,
): Promise<void> {
  await apiFetch(`/mod/ai/chat/threads/${threadId}/confirm-tool`, {
    method: 'POST',
    body: JSON.stringify({
      tool_use_id: toolUseId,
      decision,
      argument_overrides: argumentOverrides,
    }),
  });
}

// ── SSE turn stream ──────────────────────────────────────────────────

export type SseEventHandler = (eventName: string, data: Record<string, unknown>) => void;

/**
 * Stream a user-message turn from the backend. The EventSource API
 * doesn't support custom request headers (so no Bearer auth), so we
 * fall back to fetch+ReadableStream and parse SSE manually.
 *
 * The returned promise resolves when the stream ends; reject with the
 * error from the stream (or a network error). Pass an AbortSignal to
 * cancel early.
 */
export async function streamChatTurn(
  threadId: string,
  text: string,
  onEvent: SseEventHandler,
  signal?: AbortSignal,
): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  if (!token) throw new Error('Not authenticated.');

  const resp = await fetch(
    `/api/v1/mod/ai/chat/threads/${threadId}/turn`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({ text }),
      signal,
    },
  );
  if (!resp.ok) {
    const body = await resp.text();
    throw new Error(`HTTP ${resp.status}: ${body || resp.statusText}`);
  }
  const reader = resp.body?.getReader();
  if (!reader) throw new Error('No response body to stream.');

  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, nl);
      buf = buf.slice(nl + 2);
      parseAndDispatch(frame, onEvent);
    }
  }
}

function parseAndDispatch(frame: string, onEvent: SseEventHandler): void {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue; // comment / heartbeat
    if (line.startsWith('event: ')) event = line.slice(7).trim();
    else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
  }
  if (dataLines.length === 0) return;
  const raw = dataLines.join('\n');
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = { raw };
  }
  onEvent(event, parsed);
}
